/**
 * Countdown Timer
 * 
 * Lightweight countdown timer that updates in real-time.
 * Supports multiple timers on a single page.
 * Auto-cleans intervals when countdown completes.
 * 
 * @module CountdownTimer
 */

export class CountdownTimer {
  constructor(element) {
    this.element = element;
    this.endTime = new Date(element.dataset.endTime).getTime();
    this.expiredMessage = element.dataset.expiredMessage || 'This offer has ended';
    
    // Cache DOM elements
    this.daysEl = element.querySelector('[data-countdown-days]');
    this.hoursEl = element.querySelector('[data-countdown-hours]');
    this.minsEl = element.querySelector('[data-countdown-mins]');
    this.secsEl = element.querySelector('[data-countdown-secs]');
    this.srLabelEl = element.querySelector('[data-countdown-sr-label]');
    
    this.intervalId = null;
    
    // Check if already expired
    if (this.isExpired()) {
      this.handleExpired();
    } else {
      this.start();
    }
  }
  
  /**
   * Check if the countdown has expired
   */
  isExpired() {
    return Date.now() >= this.endTime;
  }
  
  /**
   * Start the countdown timer
   */
  start() {
    // Initial update
    this.update();
    
    // Update every second
    this.intervalId = setInterval(() => {
      if (this.isExpired()) {
        this.handleExpired();
      } else {
        this.update();
      }
    }, 1000);
  }
  
  /**
   * Update the countdown display
   */
  update() {
    const timeRemaining = this.calculateTimeRemaining();
    
    // Update display
    if (this.daysEl) this.daysEl.textContent = timeRemaining.days;
    if (this.hoursEl) this.hoursEl.textContent = this.padZero(timeRemaining.hours);
    if (this.minsEl) this.minsEl.textContent = this.padZero(timeRemaining.minutes);
    if (this.secsEl) this.secsEl.textContent = this.padZero(timeRemaining.seconds);
    
    // Update screen reader label
    if (this.srLabelEl) {
      this.srLabelEl.textContent = this.generateScreenReaderLabel(timeRemaining);
    }
  }
  
  /**
   * Calculate time remaining
   */
  calculateTimeRemaining() {
    const now = Date.now();
    const difference = this.endTime - now;
    
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);
    
    return { days, hours, minutes, seconds };
  }
  
  /**
   * Generate accessible screen reader label
   */
  generateScreenReaderLabel({ days, hours, minutes, seconds }) {
    const parts = [];
    
    if (days > 0) {
      parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
    }
    if (hours > 0 || days > 0) {
      parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    }
    if (minutes > 0 || hours > 0 || days > 0) {
      parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    }
    parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);
    
    return `${parts.join(', ')} remaining`;
  }
  
  /**
   * Pad single digit numbers with zero
   */
  padZero(num) {
    return String(num).padStart(2, '0');
  }
  
  /**
   * Handle expired countdown
   */
  handleExpired() {
    // Stop the interval
    this.stop();
    
    // Add expired class
    this.element.classList.add('is-expired');
    
    // Update screen reader announcement
    if (this.srLabelEl) {
      this.srLabelEl.textContent = this.expiredMessage;
    }
    
    // Dispatch custom event
    this.element.dispatchEvent(new CustomEvent('countdown:expired', {
      bubbles: true,
      detail: { element: this.element }
    }));
  }
  
  /**
   * Stop the countdown timer
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
  
  /**
   * Destroy the countdown timer
   */
  destroy() {
    this.stop();
    this.element = null;
    this.daysEl = null;
    this.hoursEl = null;
    this.minsEl = null;
    this.secsEl = null;
    this.srLabelEl = null;
  }
  
  /**
   * Initialize all countdown timers on the page
   */
  static initAll() {
    const timers = document.querySelectorAll('[data-countdown-timer]');
    const instances = [];
    
    timers.forEach(timer => {
      instances.push(new CountdownTimer(timer));
    });
    
    return instances;
  }
  
  /**
   * Initialize countdown timers with Intersection Observer
   * (lazy-init for performance when timer is offscreen)
   */
  static initLazy(options = {}) {
    const defaultOptions = {
      root: null,
      rootMargin: '50px',
      threshold: 0,
      ...options
    };
    
    const timers = document.querySelectorAll('[data-countdown-timer]');
    const instances = new Map();
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !instances.has(entry.target)) {
          const instance = new CountdownTimer(entry.target);
          instances.set(entry.target, instance);
          observer.unobserve(entry.target);
        }
      });
    }, defaultOptions);
    
    timers.forEach(timer => {
      observer.observe(timer);
    });
    
    return { instances, observer };
  }
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    CountdownTimer.initAll();
  });
} else {
  CountdownTimer.initAll();
}

// Export for module usage
export default CountdownTimer;
